import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, CheckCircle2, AlertTriangle, Loader2, ScanLine } from 'lucide-react';
import { API } from '@/utils/api';
import axios from 'axios';

const MobileScanUpload = () => {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | uploading | done | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/order-slips/scan-session/${token}`)
      .then(r => { setSession(r.data); setStatus('ready'); })
      .catch(() => { setStatus('error'); setErrorMsg('Lien invalide ou expire. Veuillez rescanner le QR code.'); });
  }, [token]);

  const handleCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatus('uploading');
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/order-slips/scan-session/${token}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || 'Erreur de telechargement');
    }
  };

  const supplierColor = session?.supplier === 'ESA' ? '#3b82f6' : session?.supplier === 'Jacquet' ? '#f59e0b' : '#10b981';

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center mx-auto mb-3">
            <ScanLine className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Bon de commande</h1>
          <p className="text-sm text-slate-500">Prenez une photo du bon</p>
        </div>

        {/* Loading */}
        {status === 'loading' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Chargement...</p>
          </div>
        )}

        {/* Ready - Show session info + capture button */}
        {status === 'ready' && session && (
          <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
            {/* Session info */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: supplierColor }}>
                  {session.supplier}
                </span>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <p className="text-sm font-bold text-slate-900">{session.vehicle_info || 'Vehicule'}</p>
                <p className="text-xs text-slate-500">{session.client_name}</p>
                <p className="text-xs text-slate-400">Fiche: {session.work_order_id?.substring(0, 8).toUpperCase()}</p>
              </div>
            </div>

            {/* Capture button - label for native camera */}
            <input
              id="mobile-capture"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleCapture}
              style={{ display: 'none' }}
            />
            <label
              htmlFor="mobile-capture"
              className="flex items-center justify-center gap-3 w-full py-5 rounded-xl bg-indigo-600 active:bg-indigo-800 text-white font-bold text-lg cursor-pointer touch-manipulation"
            >
              <Camera className="w-7 h-7" />
              Prendre la photo
            </label>
            <p className="text-xs text-slate-400 text-center">Votre appareil photo va s'ouvrir</p>
          </div>
        )}

        {/* Uploading */}
        {status === 'uploading' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700">Envoi en cours...</p>
            <p className="text-sm text-slate-500 mt-1">Ne fermez pas cette page</p>
          </div>
        )}

        {/* Done */}
        {status === 'done' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-lg font-bold text-green-700">Photo envoyee !</p>
            <p className="text-sm text-slate-500 mt-2">
              Le bon de commande {session?.supplier} a ete ajoute a la fiche de travail.
            </p>
            <p className="text-xs text-slate-400 mt-3">Vous pouvez fermer cette page.</p>

            {/* Allow taking another photo */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <input
                id="mobile-capture-again"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => { setStatus('ready'); handleCapture(e); }}
                style={{ display: 'none' }}
              />
              <label
                htmlFor="mobile-capture-again"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-700 font-medium text-sm cursor-pointer"
              >
                <Camera className="w-4 h-4" /> Autre photo
              </label>
            </div>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-lg font-bold text-red-700">Erreur</p>
            <p className="text-sm text-slate-500 mt-2">{errorMsg}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileScanUpload;
